import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { ToastContext, type Toast, type ToastType } from '../../hooks/useToast';
import { useMobileTabBarPresent } from '../../hooks/useMobileTabBarPresent';
import { MOBILE_TAB_BAR_RESERVE_PX } from '../../constants/layout';

const MAX_TOASTS = 5;
// SF-171: exported so MobileTargetDetailView's local gather-feedback banner can reuse this exact
// dismiss timing instead of re-declaring the same 5000ms constant (CLAUDE.md's SF-125 hex/const
// drift pattern, generalized to any duplicated literal).
export const AUTO_DISMISS_MS = 5000;

// SF-181: 'warning' sourced from the existing COLORS.warning token (#e0923c), not a new
// hardcoded hex (CLAUDE.md's SF-125 hex-debt discipline).
const TOAST_STYLES: Record<ToastType, { background: string; border: string }> = {
  success: {
    background: 'rgba(93,204,93,0.1)',
    border: 'rgba(93,204,93,0.3)',
  },
  failure: {
    background: 'rgba(255,85,85,0.1)',
    border: 'rgba(255,85,85,0.3)',
  },
  warning: {
    background: 'rgba(224,146,60,0.1)',
    border: 'rgba(224,146,60,0.3)',
  },
};

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message: string, type: ToastType) => {
    const id = `toast-${++toastCounter}`;
    const toast: Toast = { id, message, type };

    setToasts(prev => {
      const next = [...prev, toast];
      // Trim oldest toasts if over max
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });

    // Auto-dismiss
    const timer = setTimeout(() => {
      removeToast(id);
    }, AUTO_DISMISS_MS);
    timersRef.current.set(id, timer);
  }, [removeToast]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  // SF-181: ToastContainer renders inside ToastProvider, which sits inside the Router (App.tsx)
  // — safe to call route/platform-aware hooks directly, same as Layout.tsx/AppShellLayout.tsx.
  const mobileTabBarPresent = useMobileTabBarPresent();

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: mobileTabBarPresent ? `${MOBILE_TAB_BAR_RESERVE_PX}px` : '16px',
        right: '16px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: '400px',
      }}
    >
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const styles = TOAST_STYLES[toast.type];

  return (
    <div
      // SF-181: only a genuine action-rejected failure interrupts assistive tech with role="alert";
      // success/warning are role="status" (polite, non-interrupting).
      role={toast.type === 'failure' ? 'alert' : 'status'}
      style={{
        background: styles.background,
        border: `1px solid ${styles.border}`,
        borderRadius: '6px',
        padding: '10px 14px',
        color: '#f0e8dc',
        fontSize: '13px',
        fontFamily: 'DM Sans, sans-serif',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        animation: 'slideInRight 0.2s ease-out',
        backdropFilter: 'blur(8px)',
      }}
    >
      <span style={{ flex: 1 }}>
        {/* Non-color warning cue (WCAG 1.4.1) alongside the amber background/border. */}
        {toast.type === 'warning' ? '⚠ ' : ''}{toast.message}
      </span>
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          color: '#b5a99c',
          cursor: 'pointer',
          padding: '2px',
          fontSize: '16px',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
